import React, { useMemo, useRef, useCallback } from "react";
import { BarRounded } from "@visx/shape";
import { Group } from "@visx/group";
import { GridColumns } from "@visx/grid";
import { scaleBand, scaleLinear } from "@visx/scale";
import { scaleSequential } from "d3-scale";
import { TooltipWithBounds, defaultStyles, useTooltip } from "@visx/tooltip";
import { useSpring, useTransition, animated } from "react-spring"; // Import useTransition for list animations
import { AnimatedAxis } from "@visx/react-spring"; // For animated axis transitions
import { AxisBottom } from "@visx/axis";

import { interpolateRgbBasis } from "d3-interpolate"; // Import color interpolation
import { Flex, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  backgroundGrey,
  color2,
  textSecondary,
} from "../../../../../utils/colors";
import { tint } from "polished"; // Tinting colors for gradients
import { useTranslation } from "react-i18next";
import useNumberFormatter from "../../../../../hooks/useNumberFormatter";

const defaultMargin = { top: 0, right: 0, bottom: 0, left: 36 };

const getAgeGroup = (d) => d.ageGroup;
const getCount = (d) => d.count;

const AnimatedBar = animated(BarRounded); // Wrap BarRounded component in an animated wrapper

const AgeBarChart = ({ width, height, data }) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const formatNumber = useNumberFormatter();

  const {
    hideTooltip,
    showTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipOpen,
    tooltipTop = 0,
  } = useTooltip({
    tooltipOpen: false,
  });

  const xMax = width - defaultMargin.left - defaultMargin.right;
  const yMax = height - defaultMargin.top - defaultMargin.bottom;

  // Define the order of age groups for consistent display
  const ageGroupOrder = ["0-12", "13-25", "26-40", "41-60", "61-150"];

  // Prepare age group data with order
  const ageGroups = useMemo(() => {
    return Object.keys(data || {})
      .map((ageGroup) => ({
        ageGroup,
        count: data[ageGroup],
      }))
      .sort(
        (a, b) =>
          ageGroupOrder.indexOf(a.ageGroup) - ageGroupOrder.indexOf(b.ageGroup)
      );
  }, [data, ageGroupOrder]);

  // console.log("xMax", xMax);
  // console.log("yMax", yMax);

  // Define scales based on age group data
  const xScale = useMemo(
    () =>
      scaleLinear({
        range: [0, xMax],
        nice: true,
        domain: [0, Math.max(...ageGroups.map(getCount))], // Domain is defined by the maximum count
      }).nice(),
    [xMax, ageGroups]
  );

  const yScale = useMemo(
    () =>
      scaleBand({
        range: [0, yMax],
        round: true,
        domain: ageGroups.map(getAgeGroup), // Define Y axis based on age group order
        padding: 0.3,
      }),
    [yMax, ageGroups]
  );

  // Create a color scale using interpolated colors
  const colorInterpolator = interpolateRgbBasis([
    tint(0.8, color2),
    tint(0.5, color2),
    color2,
  ]);
  const colorScale = useMemo(
    () =>
      scaleSequential()
        .domain([0, Math.max(...ageGroups.map(getCount))])
        .interpolator(colorInterpolator), // Use custom color interpolator
    [ageGroups]
  );

  // Animate the entire chart's opacity and position on load
  const chartAnimation = useSpring({
    from: { opacity: 0, transform: "translateY(0px)" },
    to: { opacity: 1, transform: "translateY(0px)" },
    config: { duration: 500 },
  });

  // Transition each bar individually using react-spring's useTransition hook
  const transitions = useTransition(ageGroups, {
    from: { width: 0, opacity: 0 },
    enter: (item) => ({ width: xScale(item.count), opacity: 1 }),
    update: (item) => ({ width: xScale(item.count), opacity: 1 }),
    leave: { width: 0, opacity: 0 },
    keys: (item) => item.ageGroup, // Key is the age group for consistent animation
    config: { tension: 220, friction: 30 }, // Smooth animation config
  });

  const handleTooltipShow = useCallback(
    (e, d) => {
      const { current } = chartRef;
      if (current && d !== undefined) {
        const bounding = current.getBoundingClientRect();
        showTooltip({
          tooltipData: d,
          tooltipLeft: e.clientX - bounding.x,
          tooltipTop: e.clientY - bounding.y,
        });
      }
    },
    [showTooltip]
  );

  if (width < 10) return null;

  // console.log("ageGroups", ageGroups);

  // Get the maximum count for setting the width of the bars
  const maxCount = Math.max(...ageGroups.map(getCount));
  // console.log("maxCount", maxCount);

  // Change the color if all counts are zero
  const noDataColor = "#d1d1d1"; // Set to a gray color for zero data

  console.log("maxCount", maxCount);

  return (
    <animated.div style={chartAnimation}>
      <Flex w="100%" position="relative" ref={chartRef} as={motion.div}>
        <svg width={width} height={height}>
          <GridColumns
            scale={xScale}
            height={yMax}
            strokeDasharray="6"
            stroke={backgroundGrey}
            top={defaultMargin.top}
            left={defaultMargin.left}
          />

          <Group top={defaultMargin.top} left={defaultMargin.left}>
            {transitions((props, d) => (
              <AnimatedBar
                key={d.ageGroup}
                x={0}
                y={yScale(d.ageGroup)} // Use yScale to determine Y position
                height={yScale.bandwidth()}
                // width={props.width} // Use the animated width
                width={maxCount === 0 ? 20 : props.width} // Set width to 0 if count is 0
                fill={d.count === 0 ? noDataColor : colorScale(d.count)} // Use gray if no data
                opacity={props.opacity} // Use spring's opacity value
                radius={8}
                right
                onMouseMove={(e) => handleTooltipShow(e, d)}
                onMouseLeave={() => hideTooltip()}
              />
            ))}
          </Group>

          {/* X Axis */}
          <AnimatedAxis
            scale={xScale}
            top={yMax + defaultMargin.top}
            left={defaultMargin.left}
            stroke={backgroundGrey}
            orientation="bottom"
            hideTicks
            hideAxisLine
            hideZero
          />

          {/* Y Axis */}
          <AxisBottom
            top={defaultMargin.top}
            left={defaultMargin.left}
            scale={yScale}
            orientation="left"
            stroke="#C6D4DB"
            hideTicks
            tickLabelProps={() => ({
              fill: textSecondary,
              dx: "-26px",
              dy: "4px",
              fontSize: "10px",
            })}
          />
        </svg>

        {/* Tooltip */}
        {tooltipData && tooltipOpen && (
          <TooltipWithBounds
            left={tooltipLeft}
            style={{
              ...defaultStyles,
              boxShadow: "0px 2px 5px rgba(27, 47, 55, 0.2)",
              padding: "0.5rem 1rem",
              borderRadius: "1rem",
              backgroundColor: "#2D3748",
              color: "white",
              fontSize: "0.8rem",
              fontWeight: "500",
            }}
            top={tooltipTop}
          >
            <Flex align="center">
              {tooltipData.ageGroup}
              {t("common.years")} -
              <Text style={{ fontWeight: "600" }} mx={1}>
                {formatNumber(tooltipData.count)}
              </Text>{" "}
              <Text fontSize="0.8rem" textTransform="lowercase">
                {t("patient.patients")}
              </Text>
            </Flex>
          </TooltipWithBounds>
        )}
      </Flex>
    </animated.div>
  );
};

export default AgeBarChart;
